import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { authInit, authResetLoginLinkRequest } from '../core/actions';
import { useQuery } from '@shared/index';
import { LoginWrapper } from './LoginWrapper';
import { Loading } from '@components/index';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getAuthTokenSelector, getErrorSelector } from '../core/selectors';
import { FormattedMessage } from 'react-intl';
import { readToken } from '../utils/readToken';

const isTokenValid = (token: string | undefined): boolean => {
  if (!token) return false;
  try {
    const decoded = readToken(token);
    return !!decoded?.exp && decoded.exp > Date.now() / 1000;
  } catch {
    return false;
  }
};

export const LoginViaSession = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const error = useSelector(getErrorSelector);
  const accessToken = useSelector(getAuthTokenSelector);

  const { getQueryParam } = useQuery();
  const sessionToken = getQueryParam('sessionToken') || localStorage.getItem('auth.sessionToken');

  const [linkExpired, setLinkExpired] = useState(false);

  useEffect(() => {
    // Only skip the exchange if the stored accessToken is still valid (not expired).
    // A stale token from redux-persist should NOT prevent re-authentication.
    if (isTokenValid(accessToken)) {
      navigate('/');
      return;
    }

    if (sessionToken) {
      dispatch(authResetLoginLinkRequest());
      localStorage.setItem('auth.sessionToken', sessionToken);
      localStorage.setItem('auth.sessionTokenTimestamp', String(Date.now()));
      dispatch(authInit({ sessionToken }));
    }
  }, [sessionToken, dispatch, accessToken, navigate]);

  useEffect(() => {
    if (error) {
      // Session token failed (expired/consumed) — show friendly message
      setLinkExpired(true);
    }
  }, [error]);

  if (linkExpired) {
    return (
      <LoginWrapper>
        <h1>
          <span className="login-headline">
            <FormattedMessage id="AUTH.LOGIN.LINK_EXPIRED_HEADLINE" />
          </span>
        </h1>
        <p className="login-text">
          <FormattedMessage id="AUTH.LOGIN.LINK_EXPIRED" />
        </p>
        <button className="btn btn-primary w-full mt-6" onClick={() => navigate('/login')}>
          <FormattedMessage id="AUTH.LOGIN.REQUEST_NEW_LINK" />
        </button>
      </LoginWrapper>
    );
  }

  return (
    <LoginWrapper>
      <h1>
        <span className="login-msg">
          <FormattedMessage id="AUTH.SESSION.WELCOME" />
        </span>
        {/* Brand name, so it is not translated. */}
        <span className="login-msg-sup">Pfotenportal</span>
      </h1>

      <p className="login-text">
        <FormattedMessage id="AUTH.SESSION.REDIRECTING" />
      </p>
      <Loading />
    </LoginWrapper>
  );
};
